import { useEffect, useState } from "react";

type EmailCooldownAction = "verification";

const STORAGE_KEY = "layup-list-email-cooldowns";
const DEFAULT_COOLDOWN_MS = 60_000;

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function buildCooldownKey(action: EmailCooldownAction, email: string): string {
  return `${action}:${normalizeEmail(email)}`;
}

function readCooldowns(): Record<string, number> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as Record<string, number>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeCooldowns(cooldowns: Record<string, number>): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cooldowns));
  } catch {
    // Ignore localStorage write failures so auth forms still work.
  }
}

function pruneCooldowns(cooldowns: Record<string, number>, now = Date.now()): Record<string, number> {
  return Object.fromEntries(
    Object.entries(cooldowns).filter(([, expiresAt]) => Number.isFinite(expiresAt) && expiresAt > now),
  );
}

export function getEmailCooldownRemainingMs(action: EmailCooldownAction, email: string): number {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    return 0;
  }

  const now = Date.now();
  const cooldowns = pruneCooldowns(readCooldowns(), now);
  writeCooldowns(cooldowns);
  const expiresAt = cooldowns[buildCooldownKey(action, normalizedEmail)] ?? 0;
  return Math.max(0, expiresAt - now);
}

export function startEmailCooldown(
  action: EmailCooldownAction,
  email: string,
  durationMs = DEFAULT_COOLDOWN_MS,
): void {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    return;
  }

  const now = Date.now();
  const cooldowns = pruneCooldowns(readCooldowns(), now);
  cooldowns[buildCooldownKey(action, normalizedEmail)] = now + durationMs;
  writeCooldowns(cooldowns);
}

export function useEmailCooldown(action: EmailCooldownAction, email: string): number {
  const [remainingMs, setRemainingMs] = useState(() => getEmailCooldownRemainingMs(action, email));

  useEffect(() => {
    function updateRemaining(): void {
      setRemainingMs(getEmailCooldownRemainingMs(action, email));
    }

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(interval);
  }, [action, email]);

  return remainingMs;
}

export function formatCooldownMessage(remainingMs: number): string {
  const remainingSeconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return `We already sent a verification email for that Dartmouth address. Please wait ${remainingSeconds}s before trying again. If you do not see it in your inbox, please check your junk or spam folder.`;
}
